import { Link, useParams } from "react-router-dom";
import { platformServicePath, useApi } from "../api/client";
import type {
    IntelligencePanel,
    McpPanel,
    RagPanel,
    RouterPanel,
} from "../api/types";
import { Meter } from "../components/Charts";
import { RagQueryProbe } from "../components/RagQueryProbe";
import { CostCell } from "../components/CostCell";
import { Bar, Empty, ErrorState, Loading } from "../components/States";
import { Breadcrumb, Toolbar } from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtCost, fmtDateTime, fmtMs, fmtTokens, humanise } from "../lib/format";

const TITLES: Record<string, string> = {
    intelligence: "Intelligence Layer",
    rag: "RAG Service",
    mcp: "MCP Server",
    router: "LLM Router",
};

export function PlatformService() {
    const { key = "" } = useParams();
    const known = key in TITLES;
    const { data, error, loading, reload } = useApi<unknown>(
        known ? platformServicePath(key) : null
    );

    if (!known) {
        return (
            <div className="card">
                <Empty title="Unknown service" hint="Pick one from the Platform page." />
            </div>
        );
    }

    return (
        <>
            <Breadcrumb
                trail={[{ label: "Platform", to: "/platform" }, { label: TITLES[key] }]}
            />

            {loading ? (
                <div className="card"><Loading what={TITLES[key]} /></div>
            ) : error ? (
                <div className="card"><ErrorState message={error} onRetry={reload} /></div>
            ) : !data ? null : key === "router" ? (
                <RouterView d={data as RouterPanel} />
            ) : key === "intelligence" ? (
                <IntelligenceView d={data as IntelligencePanel} />
            ) : key === "mcp" ? (
                <McpView d={data as McpPanel} />
            ) : (
                <RagView d={data as RagPanel} />
            )}
        </>
    );
}

/** Shown whenever a service answered but the panel could not be filled. */
function Unreachable({ error }: { error: string | null }) {
    return (
        <div className="notice notice-warn">
            <strong>This service did not answer.</strong>{" "}
            {error ?? "No detail was returned."} The figures below come from the database and
            are still accurate; anything sourced live from the service is missing.
        </div>
    );
}

/* ---------------------------------------------------------------- router --- */

function RouterView({ d }: { d: RouterPanel }) {
    const reg = d.providers?.registry ?? {};
    const cb = d.providers?.circuit_breaker ?? {};
    const q = d.quality;

    // In flight vs abandoned, not one "pending" figure. A decision closes
    // within seconds of being opened, so anything older was abandoned — almost
    // always because the provider threw before the report could be sent.
    const inFlight = d.unresolved?.in_flight ?? 0;
    const abandoned = d.unresolved?.abandoned ?? 0;
    const maxDecisions = Math.max(1, ...d.decisions.map((t) => t.decisions));

    return (
        <>
            <div className="page-head">
                <div>
                    <h1 className="page-title">LLM Router</h1>
                    <p className="page-sub">
                        Which provider and tier each call went to, and what it cost.
                    </p>
                </div>
            </div>

            {!d.reachable ? <Unreachable error={d.error} /> : null}

            <div className="tiles">
                <div className="tile">
                    <div className="tile-label">Routing decisions</div>
                    <div className="tile-value">{fmtTokens(d.total_decisions)}</div>
                    <div className="tile-note">{d.decisions.length} tier/model combinations</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Executable providers</div>
                    <div className="tile-value">
                        {(reg.executable_providers ?? []).length}
                        <span className="muted" style={{ fontSize: 15, fontWeight: 500 }}>
                            {" "}/ {(reg.implemented_providers ?? []).length} implemented
                        </span>
                    </div>
                    <div className="tile-note">
                        {(reg.providers_disabled_by_config ?? []).length} disabled by config
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">In flight</div>
                    <div className="tile-value" style={inFlight ? { color: "var(--serious)" } : undefined}>
                        {fmtTokens(inFlight)}
                    </div>
                    <div className="tile-note">
                        {inFlight
                            ? "opened in the last hour, not yet closed"
                            : abandoned
                              ? `${abandoned} abandoned earlier`
                              : "every decision closed"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Schema valid, first pass</div>
                    <div className="tile-value">
                        {q.schema_ok_first_pass + q.schema_failed_first_pass === 0
                            ? EM_DASH
                            : `${Math.round(
                                  (q.schema_ok_first_pass /
                                      (q.schema_ok_first_pass + q.schema_failed_first_pass)) *
                                      100
                              )}%`}
                    </div>
                    <div className="tile-note">
                        {q.schema_ok_first_pass} of{" "}
                        {q.schema_ok_first_pass + q.schema_failed_first_pass} graded
                    </div>
                </div>
            </div>

            {inFlight > 0 ? (
                <div className="notice notice-warn">
                    <strong>{inFlight} decision{inFlight === 1 ? "" : "s"} opened but not closed
                    in the last hour.</strong>{" "}
                    A decision normally closes within seconds of the provider answering, so this
                    is worth watching — cost and latency are not recorded until it closes.
                </div>
            ) : null}

            {abandoned > 0 ? (
                <div className="notice">
                    <strong>{abandoned} of {d.total_decisions} decisions were abandoned</strong>{" "}
                    — opened, never closed, and too old to still be running. Almost all date from
                    the window when every Azure call was failing on an unsupported API version:
                    the provider threw before the outcome could be reported. Nothing has been
                    abandoned since that was fixed, but their cost and latency are permanently
                    unrecorded, which is why router spend reads lower than the token ledger.
                </div>
            ) : null}

            <div className="grid-2">
                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Provider registry</span>
                    </div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>Executable</dt>
                            <dd>
                                {(reg.executable_providers ?? []).map((p) => (
                                    <span className="pill pill-good" key={p} style={{ marginRight: 6 }}>{p}</span>
                                ))}
                                {!(reg.executable_providers ?? []).length ? EM_DASH : null}
                            </dd>
                            <dt>Disabled by config</dt>
                            <dd>
                                {(reg.providers_disabled_by_config ?? []).map((p) => (
                                    <span className="pill pill-warning" key={p} style={{ marginRight: 6 }}>{p}</span>
                                ))}
                                {!(reg.providers_disabled_by_config ?? []).length ? EM_DASH : null}
                            </dd>
                            <dt>Declared, unavailable</dt>
                            <dd>
                                {(reg.declared_but_unavailable ?? []).map((p) => (
                                    <span className="pill" key={p} style={{ marginRight: 6 }}>{p}</span>
                                ))}
                                {!(reg.declared_but_unavailable ?? []).length ? EM_DASH : null}
                            </dd>
                        </dl>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Circuit breaker</span>
                        <span className="muted">{cb.backend ?? EM_DASH}</span>
                    </div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>Failure threshold</dt>
                            <dd>{cb.failure_threshold ?? EM_DASH}</dd>
                            <dt>Open window</dt>
                            <dd>{cb.open_seconds ? `${cb.open_seconds}s` : EM_DASH}</dd>
                            {Object.entries(cb.providers ?? {}).map(([p, v]) => (
                                <div key={p} style={{ display: "contents" }}>
                                    <dt>{p}</dt>
                                    <dd>
                                        <span
                                            className={`pill ${v.state === "closed" ? "pill-good" : "pill-critical"}`}
                                        >
                                            {v.state ?? "unknown"}
                                        </span>{" "}
                                        <span className="muted">{v.failures ?? 0} failures</span>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>

            <Toolbar
                title="Routing Decisions"
                subtitle="By tier, provider and model"
                onExport={
                    d.decisions.length
                        ? () =>
                              downloadCsv(
                                  stampedName("router-decisions"),
                                  toCsv(d.decisions, [
                                      { header: "Tier", value: (t) => t.tier },
                                      { header: "Provider", value: (t) => t.provider },
                                      { header: "Model", value: (t) => t.model },
                                      { header: "Decisions", value: (t) => t.decisions },
                                      { header: "Cache hits", value: (t) => t.cache_hits },
                                      { header: "Failovers", value: (t) => t.failovers },
                                      { header: "p50 ms", value: (t) => t.p50_ms },
                                      { header: "Tokens", value: (t) => t.tokens },
                                      { header: "Cost USD", value: (t) => t.cost_usd },
                                      { header: "Unpriced calls", value: (t) => t.unpriced },
                                  ])
                              )
                        : undefined
                }
            />
            <div className="card">
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col" className="si">Sl</th>
                                <th scope="col">Tier</th>
                                <th scope="col">Provider / model</th>
                                <th scope="col" className="num">Decisions</th>
                                <th scope="col" style={{ width: 120 }}>Share</th>
                                <th scope="col" className="num">Cache hits</th>
                                <th scope="col" className="num">Failovers</th>
                                <th scope="col" className="num">p50</th>
                                <th scope="col" className="num">Tokens</th>
                                <th scope="col" className="num">Cost</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.decisions.map((t, i) => (
                                <tr key={`${t.tier}-${t.model}-${i}`}>
                                    <td className="si">{i + 1}</td>
                                    <td className="nowrap">{humanise(t.tier)}</td>
                                    <td>
                                        <div className="mono">{t.provider}</div>
                                        <div className="mono muted">{t.model}</div>
                                    </td>
                                    <td className="num">{fmtTokens(t.decisions)}</td>
                                    <td><Bar value={t.decisions} max={maxDecisions} /></td>
                                    <td className="num">{fmtTokens(t.cache_hits)}</td>
                                    <td className="num">
                                        {t.failovers ? (
                                            <span className="pill pill-warning">{t.failovers}</span>
                                        ) : "0"}
                                    </td>
                                    {/* 0ms is not a fast call: latency was never recorded on
                                        those rows. */}
                                    <td className="num">
                                        {t.p50_ms ? fmtMs(t.p50_ms) : <span className="muted">not recorded</span>}
                                    </td>
                                    <td className="num">{fmtTokens(t.tokens)}</td>
                                    <td className="num">
                                        <CostCell usd={t.cost_usd} recorded={t.unpriced < t.decisions} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="grid-2">
                <div className="card">
                    <div className="card-head bordered"><span className="card-title">Routing reasons</span></div>
                    <div className="card-body">
                        {d.reasons.map((r) => (
                            <Meter
                                key={r.reason}
                                label={r.reason}
                                value={(r.n / Math.max(1, d.total_decisions)) * 100}
                                display={`${r.n}`}
                            />
                        ))}
                    </div>
                </div>
                <div className="card">
                    <div className="card-head bordered"><span className="card-title">Outcomes</span></div>
                    <div className="card-body">
                        {d.outcomes.map((o) => (
                            <Meter
                                key={o.outcome}
                                label={o.outcome}
                                value={(o.n / Math.max(1, d.total_decisions)) * 100}
                                display={`${o.n}`}
                                color={
                                    o.outcome === "SUCCESS" ? "var(--good)"
                                    : o.outcome === "ERROR" ? "var(--critical)"
                                    : o.outcome === "IN_FLIGHT" ? "var(--serious)"
                                    : o.outcome === "ABANDONED" ? "var(--ink-muted)"
                                    : "var(--s1)"
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

/* ---------------------------------------------------------- intelligence --- */

function IntelligenceView({ d }: { d: IntelligencePanel }) {
    const t = d.tier2;
    return (
        <>
            <div className="page-head">
                <div>
                    <h1 className="page-title">Intelligence Layer</h1>
                    <p className="page-sub">
                        Knowledge packs, the rules they carry, and the verdict each document earned.
                    </p>
                </div>
            </div>

            {!d.reachable ? <Unreachable error={d.error} /> : null}

            <div className="tiles">
                <div className="tile">
                    <div className="tile-label">Document types</div>
                    <div className="tile-value">{d.document_types.length}</div>
                    <div className="tile-note">{d.packs_in_use.length} packs actually used</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Document verdicts</div>
                    <div className="tile-value">{fmtTokens(t.document_verdicts)}</div>
                    <div className="tile-note">Tier 1, stored on each extraction</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Application verdicts</div>
                    <div
                        className="tile-value"
                        style={t.application_verdicts === 0 ? { color: "var(--serious)" } : undefined}
                    >
                        {fmtTokens(t.application_verdicts)}
                    </div>
                    <div className={`tile-note${t.application_verdicts === 0 ? " warn" : ""}`}>
                        {t.application_verdicts === 0 ? "Tier 2 has never run" : "Tier 2"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Pack changes</div>
                    <div className="tile-value">{fmtTokens(d.audit.length)}</div>
                    <div className="tile-note">audited seeds and edits</div>
                </div>
            </div>

            {t.note ? <div className="notice notice-warn">{t.note}</div> : null}

            <div className="grid-2">
                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Verdict distribution</span>
                        <span className="muted">document level</span>
                    </div>
                    <div className="card-body">
                        {t.by_status.length === 0 ? (
                            <Empty title="No verdicts stored" />
                        ) : (
                            t.by_status.map((v) => (
                                <Meter
                                    key={v.status}
                                    label={v.status}
                                    value={(v.n / Math.max(1, t.document_verdicts)) * 100}
                                    display={`${v.n}`}
                                    color={
                                        v.status === "VALID" ? "var(--good)"
                                        : v.status === "SUSPECT" ? "var(--warning)"
                                        : v.status === "EXPIRING_SOON" ? "var(--serious)"
                                        : "var(--critical)"
                                    }
                                />
                            ))
                        )}
                    </div>
                </div>

                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Registered document types</span>
                        <span className="muted">{d.document_types.length}</span>
                    </div>
                    <div className="card-body">
                        <div className="chips">
                            {d.document_types.map((dt) => (
                                <Link className="chip" key={dt} to={`/agents/${dt}`}>{humanise(dt)}</Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <Toolbar
                title="Knowledge Packs In Use"
                subtitle="Only packs that have actually been applied to an extraction"
                onExport={
                    d.packs_in_use.length
                        ? () =>
                              downloadCsv(
                                  stampedName("knowledge-packs"),
                                  toCsv(d.packs_in_use, [
                                      { header: "Document type", value: (p) => p.document_type },
                                      { header: "Jurisdiction", value: (p) => p.jurisdiction },
                                      { header: "Pack version", value: (p) => p.version },
                                      { header: "Extractions", value: (p) => p.extractions },
                                      { header: "Last used", value: (p) => p.last_used },
                                  ])
                              )
                        : undefined
                }
            />
            <div className="card">
                {d.packs_in_use.length === 0 ? (
                    <Empty title="No pack has been applied yet" />
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th scope="col" className="si">Sl</th>
                                    <th scope="col">Document type</th>
                                    <th scope="col">Jurisdiction</th>
                                    <th scope="col">Pack version</th>
                                    <th scope="col" className="num">Extractions</th>
                                    <th scope="col">Last used</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.packs_in_use.map((p, i) => (
                                    <tr key={`${p.document_type}-${p.version}`}>
                                        <td className="si">{i + 1}</td>
                                        <td>
                                            <Link to={`/agents/${p.document_type}`}>
                                                {humanise(p.document_type)}
                                            </Link>
                                        </td>
                                        <td>{p.jurisdiction ?? EM_DASH}</td>
                                        <td className="mono">{p.version ?? EM_DASH}</td>
                                        <td className="num">{fmtTokens(p.extractions)}</td>
                                        <td className="nowrap muted">{fmtDateTime(p.last_used)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

/* ------------------------------------------------------------------- mcp --- */

function McpView({ d }: { d: McpPanel }) {
    const totalCalls = d.usage.reduce((s, u) => s + u.calls, 0);
    const refused = d.usage.reduce((s, u) => s + u.refused, 0);
    const unavailable = d.usage.reduce((s, u) => s + u.unavailable, 0);
    const succeeded = d.usage.reduce((s, u) => s + u.success, 0);
    const stale = d.activity.total > 0 && d.activity.last_7d === 0;

    return (
        <>
            <div className="page-head">
                <div>
                    <h1 className="page-title">MCP Server</h1>
                    <p className="page-sub">
                        The tool gateway other services call, and how those calls have gone.
                    </p>
                </div>
            </div>

            {!d.reachable ? <Unreachable error={d.error} /> : null}

            <div className="tiles">
                <div className="tile">
                    <div className="tile-label">Tools registered</div>
                    <div className="tile-value">{d.tool_count ?? d.tools.length}</div>
                    <div className="tile-note">each delegates to a real service</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Recorded calls</div>
                    <div className="tile-value">{fmtTokens(totalCalls)}</div>
                    <div className={`tile-note${stale ? " warn" : ""}`}>
                        {d.activity.last_call
                            ? `last ${fmtDateTime(d.activity.last_call)}`
                            : "never called"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Refused requests</div>
                    <div className="tile-value">{fmtTokens(refused)}</div>
                    {/* Not counted as failures: these are the typed-parameter and
                        auth guards rejecting bad input, which is them working. */}
                    <div className="tile-note">bad parameters or missing credential</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Upstream unavailable</div>
                    <div
                        className="tile-value"
                        style={unavailable ? { color: "var(--serious)" } : undefined}
                    >
                        {fmtTokens(unavailable)}
                    </div>
                    <div className="tile-note">
                        {succeeded ? `${succeeded} succeeded` : "a real fault, unlike a refusal"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Authentication</div>
                    <div className="tile-value" style={{ fontSize: 18 }}>
                        <span className={`pill pill-${d.auth.severity}`}>
                            {d.auth.credentials_configured ? "Configured" : "Not set"}
                        </span>
                    </div>
                    <div className="tile-note">write tools gated</div>
                </div>
            </div>

            {stale ? (
                <div className="notice">
                    <strong>No MCP traffic in the last 7 days.</strong> All{" "}
                    {d.activity.total} logged calls are from{" "}
                    {fmtDateTime(d.activity.last_call)} or earlier, so the figures below describe
                    a past session rather than current behaviour.
                    {refused > 0 ? (
                        <>
                            {" "}Most of them are <strong>refusals</strong> — the parameter and
                            credential guards rejecting deliberately bad requests, which is those
                            guards working, not the tools failing.
                        </>
                    ) : null}
                </div>
            ) : null}

            {unavailable > 0 ? (
                <div className="notice notice-warn">
                    <strong>{unavailable} call{unavailable === 1 ? "" : "s"} could not reach an
                    upstream service.</strong> Unlike a refusal, this is a real fault: the tool
                    was dispatched correctly and the service behind it did not answer. Check the
                    messages below for which one.
                </div>
            ) : null}

            <div className="notice">{d.auth.detail}</div>

            <Toolbar title="Tool Registry" subtitle={`${d.tools.length} tools`} />
            <div className="agent-grid">
                {d.tools.map((t) => {
                    const name = String(t.name ?? "unnamed");
                    const stats = d.usage.find((u) => u.tool === name);
                    return (
                        <div className="agent-card" key={name} style={{ cursor: "default" }}>
                            <span className="agent-card-top">
                                <span className="pill pill-mono">{name}</span>
                                {stats ? (
                                    // Amber only for a genuine fault. A tool whose
                                    // calls were all refused is a tool that was
                                    // called wrongly, which says nothing about the
                                    // tool's own health.
                                    <span
                                        className={`pill ${
                                            stats.unavailable > 0 ? "pill-serious"
                                            : stats.success > 0 ? "pill-good"
                                            : "pill-warning"
                                        }`}
                                    >
                                        {stats.calls} call{stats.calls === 1 ? "" : "s"}
                                    </span>
                                ) : (
                                    <span className="pill">unused</span>
                                )}
                            </span>
                            <h3>{humanise(name.replace(/^tool_/, ""))}</h3>
                            <p>{String(t.description ?? "No description registered.")}</p>
                            <span className="agent-card-foot">
                                <span className="muted" style={{ fontSize: 12 }}>
                                    {!stats
                                        ? "never called"
                                        : [
                                              stats.success ? `${stats.success} ok` : null,
                                              stats.refused ? `${stats.refused} refused` : null,
                                              stats.unavailable
                                                  ? `${stats.unavailable} unavailable`
                                                  : null,
                                          ]
                                              .filter(Boolean)
                                              .join(" · ")}
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>

            <Toolbar
                title="Recent Calls"
                subtitle="The server's own outcome and message for each — a refusal names what was wrong with the request"
                onExport={
                    d.recent.length
                        ? () =>
                              downloadCsv(
                                  stampedName("mcp-calls"),
                                  toCsv(d.recent, [
                                      { header: "Time", value: (r) => r.created_at },
                                      { header: "Tool", value: (r) => r.tool_name },
                                      { header: "Outcome", value: (r) => r.outcome },
                                      { header: "Message", value: (r) => r.error_message },
                                      { header: "Duration ms", value: (r) => r.execution_time_ms },
                                      { header: "Correlation id", value: (r) => r.correlation_id },
                                  ])
                              )
                        : undefined
                }
            />
            <div className="card">
                {d.recent.length === 0 ? (
                    <Empty title="No calls recorded" />
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th scope="col" className="si">Sl</th>
                                    <th scope="col">Time</th>
                                    <th scope="col">Tool</th>
                                    <th scope="col">Outcome</th>
                                    <th scope="col" className="cell-prose">Message</th>
                                    <th scope="col" className="num">Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.recent.map((r, i) => (
                                    <tr key={`${r.created_at}-${i}`}>
                                        <td className="si">{i + 1}</td>
                                        <td className="nowrap muted">{fmtDateTime(r.created_at)}</td>
                                        <td className="mono nowrap">{r.tool_name ?? EM_DASH}</td>
                                        <td className="nowrap">
                                            <span
                                                className={`pill ${
                                                    r.outcome === "SUCCESS" ? "pill-good"
                                                    : r.outcome === "UNAVAILABLE" ? "pill-serious"
                                                    : r.outcome === "ERROR" ? "pill-warning"
                                                    : ""
                                                }`}
                                            >
                                                {r.outcome ?? "not recorded"}
                                            </span>
                                        </td>
                                        <td className="cell-prose">
                                            {r.error_message ? (
                                                <div className="snippet" title={r.error_message}>
                                                    {r.error_message}
                                                </div>
                                            ) : (
                                                <span className="muted">{EM_DASH}</span>
                                            )}
                                        </td>
                                        <td className="num">{fmtMs(r.execution_time_ms)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

/* ------------------------------------------------------------------- rag --- */

function RagView({ d }: { d: RagPanel }) {
    const e = d.embedding_usage;
    const maxChunks = Math.max(1, ...d.collections.map((c) => c.chunks));
    const empty = d.collections.filter((c) => c.chunks === 0);

    return (
        <>
            <div className="page-head">
                <div>
                    <h1 className="page-title">RAG Service</h1>
                    <p className="page-sub">
                        Vector collections behind retrieval, and what embedding them cost.
                    </p>
                </div>
            </div>

            {!d.reachable ? <Unreachable error={d.error} /> : null}

            <div className="tiles">
                <div className="tile">
                    <div className="tile-label">Collections</div>
                    <div className="tile-value">{d.collections.length}</div>
                    <div className={`tile-note${empty.length ? " warn" : ""}`}>
                        {empty.length ? `${empty.length} empty` : "all populated"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Indexed chunks</div>
                    <div className="tile-value">{fmtTokens(d.total_chunks)}</div>
                    <div className="tile-note">across all collections</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Embedding calls</div>
                    <div className="tile-value">{fmtTokens(e.calls)}</div>
                    <div className="tile-note">{fmtTokens(e.tokens)} tokens</div>
                </div>
                <div className="tile">
                    <div className="tile-label">Embedding cost</div>
                    <div className="tile-value">{fmtCost(e.cost_usd)}</div>
                    <div className="tile-note">{e.model ?? "no model recorded"}</div>
                </div>
            </div>

            {empty.length ? (
                <div className="notice">
                    {empty.length} of {d.collections.length} collections have no chunks
                    ({empty.map((c) => c.name).join(", ")}). They are declared but nothing has
                    been indexed into them, so retrieval against them returns nothing.
                </div>
            ) : null}

            <RagQueryProbe collections={d.collections.map((c) => c.name)} />

            <Toolbar
                title="Collections"
                subtitle={`${d.total_chunks} chunks indexed`}
                onExport={
                    d.collections.length
                        ? () =>
                              downloadCsv(
                                  stampedName("rag-collections"),
                                  toCsv(d.collections, [
                                      { header: "Collection", value: (c) => c.name },
                                      { header: "Chunks", value: (c) => c.chunks },
                                      { header: "Id", value: (c) => c.id },
                                  ])
                              )
                        : undefined
                }
            />
            <div className="card">
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col" className="si">Sl</th>
                                <th scope="col">Collection</th>
                                <th scope="col" className="num">Chunks</th>
                                <th scope="col" style={{ width: 160 }}>Share</th>
                                <th scope="col">Id</th>
                            </tr>
                        </thead>
                        <tbody>
                            {d.collections.map((c, i) => (
                                <tr key={c.id}>
                                    <td className="si">{i + 1}</td>
                                    <td>{c.name}</td>
                                    <td className="num">{fmtTokens(c.chunks)}</td>
                                    <td><Bar value={c.chunks} max={maxChunks} /></td>
                                    <td className="mono muted">{c.id.slice(0, 8)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
